import dayjs from "dayjs";

import { joinStrings, pluralize } from "./text";

export const getDifference = (
  startDate: Date,
  endDate: Date,
  unit: "day" | "hour",
  float = false,
): number => dayjs(endDate).diff(startDate, unit, float);

export const getDaysBetween = (startDate: Date, endDate: Date): number =>
  Math.abs(getDifference(endDate, startDate, "day"));

export const getDaysAndHoursBetween = (
  startDate: Date,
  endDate: Date,
): { days: number; hours: number } => {
  const totalHours = Math.abs(getDifference(startDate, endDate, "hour"));
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;

  return { days, hours };
};

export const getDaysLeft = (toDate: Date): number =>
  Math.floor(getDifference(new Date(), toDate, "day", true));

export const getHoursLeft = (toDate: Date): number =>
  Math.ceil(getDifference(new Date(), toDate, "hour", true));

export const getHoursBetween = (startDate: Date, endDate: Date): number =>
  Math.ceil(getDifference(startDate, endDate, "hour", true));

export const isOverdue = (toDate: Date): boolean => getDaysLeft(toDate) < 0;

export function formatDurationInDays(value: number) {
  if (value === 1) return "1 day";

  return `${value} days`;
}

export function formatDurationInDaysAndHours(value: {
  days: number;
  hours: number;
}) {
  const { days, hours } = value;

  const parts: string[] = [];

  if (days > 0) parts.push(days === 1 ? "1 day" : `${days} days`);
  if (hours > 0) parts.push(hours === 1 ? "1 hour" : `${hours} hours`);

  return parts.length ? parts.join(" ") : "0 hours";
}

export function formatLearnerCount(count: number) {
  if (count === 1) return "1 learner";

  return `${count} learners`;
}

export function formatDaysLeft(daysLeft: number, isExtended = false) {
  if (daysLeft < 0) return "Overdue";

  if (daysLeft === 0) return "Due today";

  const formattedDaysLeft = formatDurationInDays(daysLeft);

  return isExtended ? `${formattedDaysLeft} left` : formattedDaysLeft;
}

export const toUsaDateFormat = (date: Date): string =>
  dayjs(date).format("MM/DD/YYYY");

export const toUsaDateTimeFormat = (date: Date): string =>
  dayjs(date).format("MM/DD/YYYY h:mm A");

export function formatDurationInHours(numberOfHours: number) {
  const start = dayjs();
  const end = start.clone().add(numberOfHours, "hour");

  const diff = dayjs.duration(end.diff(start));

  const days = Math.floor(diff.asDays());
  const hours = Math.floor(diff.asHours() % 24);

  const formattedDays = days > 0 && pluralize(days, "day");
  const formattedHours = hours > 0 && pluralize(hours, "hour");

  return joinStrings([formattedDays, formattedHours]) || "0 hours";
}

export function formatDurationInMinutes(minutes: number): string {
  if (minutes <= 0) return "0 minutes";

  return `${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
}

export function formatTimeLeft(
  date: Date,
  noTimeLeftText = "No time left",
  leadingText?: string,
): string {
  const daysLeft = getDaysLeft(date);

  if (daysLeft < 0) return noTimeLeftText;

  const prependLeadingText = (string: string) =>
    joinStrings([leadingText, string]);

  if (daysLeft == 0)
    return prependLeadingText(pluralize(getHoursLeft(date), "hour"));

  return prependLeadingText(pluralize(daysLeft, "day"));
}

export function formatCourseAvailability(
  endDate: Date,
  isExtended = false,
): string {
  return formatTimeLeft(
    endDate,
    "Not available",
    isExtended ? "Available" : undefined,
  );
}

export function convertLocalDateToUtcIso(date: Date) {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();

  return new Date(Date.UTC(year, month, day));
}
